<script lang="ts">
    // Bloco "Repetir agendamento". Em um agendamento novo configura a repetição; em um já salvo que
    // faz parte de uma série, lista as datas da série e permite abrir ou excluir cada uma.
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import dayjs from 'dayjs'
    import type { Appointment } from '../../../../modules/schedulerApi/appointments/appointment'
    import { AppointmentStatusFriendly } from '../../../../modules/schedulerApi/appointments/appointments.enum'
    import ZsheetSection from '../../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetField from '../../../_common/z/sheet/zsheet-field.svelte'

    const dispatch = createEventDispatcher()

    export let entity: Appointment

    const days = [
        { label: 'dom', value: 0 },
        { label: 'seg', value: 1 },
        { label: 'ter', value: 2 },
        { label: 'qua', value: 3 },
        { label: 'qui', value: 4 },
        { label: 'sex', value: 5 },
        { label: 'sab', value: 6 },
    ]

    const periods: { label: string; value: number }[] = [
        { label: 'toda semana', value: 7 },
        { label: 'semana sim/semana não', value: 14 },
    ]
    for (let index = 3; index <= 51; index++) periods.push({ label: 'A cada ' + index + ' semanas', value: index * 7 })
    periods.push({ label: 'A cada 52 semanas (1 ano)', value: 52 * 7 })

    // um agendamento novo repete quando a quantidade é maior que zero
    $: isNew = !entity.appointmentId
    $: repeating = isNew && entity.repeat?.quantity > 0
    $: recurrencies = entity.recurrencies || []

    // vazio = repete em qualquer dia que caia na frequência escolhida
    function toggleDay(value: number) {
        const current = entity.repeat.weekDays || []
        entity.repeat.weekDays = current.includes(value) ? current.filter((el) => el !== value) : [...current, value]
        dispatch('change')
    }

    function toggleRepeat() {
        if (!entity.repeat) return
        entity.repeat.quantity = repeating ? 0 : 1
        if (!repeating && !entity.repeat.periodicity) entity.repeat.periodicity = 7
        entity = entity
        dispatch('change')
    }
</script>

{#if isNew || recurrencies.length > 0}
    <!-- no agendamento novo o próprio interruptor já diz o que é; o rótulo do bloco só aparece na série -->
    <ZsheetSection label={isNew ? '' : 'Agendamentos recorrentes'}>
        {#if isNew}
            <button type="button" class="switch" role="switch" aria-checked={repeating} on:click={toggleRepeat}>
                <span class="switch_text">
                    <span class="switch_label">Repetir agendamento</span>
                    <span class="switch_sub">{repeating ? 'Repete nas próximas semanas' : 'Uma única vez'}</span>
                </span>
                <span class="switch_track" class:on={repeating}><span class="switch_knob"></span></span>
            </button>

            {#if repeating}
                <div class="repeat" transition:slide={{ duration: 160 }}>
                    <div class="repeat_row">
                        <ZsheetField label="Repetir por mais">
                            <select bind:value={entity.repeat.quantity} on:change={() => dispatch('change')}>
                                {#each Array(52) as _, i}
                                    <option value={i + 1}>{i + 1} {i === 0 ? 'vez' : 'vezes'}</option>
                                {/each}
                            </select>
                        </ZsheetField>
                        <ZsheetField label="Frequência">
                            <select bind:value={entity.repeat.periodicity} on:change={() => dispatch('change')}>
                                {#each periods as period}
                                    <option value={period.value}>{period.label}</option>
                                {/each}
                            </select>
                        </ZsheetField>
                    </div>
                    <div class="weekdays">
                        <div class="weekdays_label">Repetir apenas nesses dias</div>
                        <div class="weekdays_items">
                            {#each days as day}
                                <button type="button" class="weekday" class:on={(entity.repeat.weekDays || []).includes(day.value)} on:click={() => toggleDay(day.value)}>
                                    {day.label}
                                </button>
                            {/each}
                        </div>
                    </div>
                </div>
            {/if}
        {:else}
            <div class="series">
                {#each recurrencies as recurrency}
                    <div class="series_item" class:current={recurrency.appointmentId === entity.appointmentId}>
                        <div class="series_text">
                            <div class="series_date">
                                {recurrency.groupPos}/{recurrency.groupTotal} · {dayjs(recurrency.start).format('DD/MM/YYYY HH:mm')}
                            </div>
                            <div class="series_status">{AppointmentStatusFriendly[recurrency.status] || ''}</div>
                        </div>
                        {#if recurrency.appointmentId === entity.appointmentId}
                            <span class="series_tag">atual</span>
                        {:else}
                            <button
                                type="button"
                                class="series_btn"
                                title="Excluir este"
                                on:click={() => dispatch('removeAnother', recurrency.appointmentId)}
                            >
                                <Fa icon={faTrashAlt} />
                            </button>
                            <button type="button" class="series_btn" title="Abrir este" on:click={() => dispatch('editAnother', recurrency.appointmentId)}>
                                <Fa icon={faEdit} />
                            </button>
                        {/if}
                    </div>
                {/each}
            </div>
        {/if}
    </ZsheetSection>
{/if}

<style>
    /* valores base = tema escuro */
    .switch {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        text-align: left;
        background: none;
        border: 0px;
        padding: 0px;
        color: inherit;
        font: inherit;
    }
    .switch_text {
        flex: 1 1 auto;
        min-width: 0;
    }
    .switch_label {
        display: block;
        font-size: 14.5px;
        line-height: 1.25;
    }
    .switch_sub {
        display: block;
        font-size: 12.5px;
        opacity: 0.55;
        line-height: 1.3;
        margin-top: 1px;
    }
    .switch_track {
        flex: 0 0 auto;
        width: 44px;
        height: 26px;
        border-radius: 999px;
        background: #ffffff26;
        position: relative;
        transition: background 0.15s ease;
    }
    .switch_track.on {
        background: #2ecc71;
    }
    .switch_knob {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #fff;
        transition: transform 0.15s ease;
    }
    .switch_track.on .switch_knob {
        transform: translateX(18px);
    }

    .repeat {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 12px;
    }
    .repeat_row {
        display: flex;
        gap: 10px;
    }

    .weekdays_label {
        font-size: 12px;
        opacity: 0.55;
        margin-bottom: 4px;
    }
    .weekdays_items {
        display: flex;
        gap: 4px;
    }
    .weekday {
        flex: 1 1 0;
        border: 1px solid #ffffff1f;
        background: #ffffff08;
        color: inherit;
        font: inherit;
        font-size: 13px;
        border-radius: 8px;
        padding: 8px 0px;
        min-width: 0;
    }
    .weekday.on {
        background: #2ecc711f;
        border-color: #2ecc7166;
    }

    .series {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .series_item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border-radius: 10px;
        background: #ffffff08;
    }
    .series_item.current {
        border: 1px solid #2ecc7166;
    }
    .series_text {
        flex: 1 1 auto;
        min-width: 0;
    }
    .series_date {
        font-size: 14px;
    }
    .series_status {
        font-size: 12px;
        opacity: 0.55;
    }
    .series_tag {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        opacity: 0.6;
    }
    .series_btn {
        flex: 0 0 auto;
        width: 32px;
        height: 32px;
        border: 0px;
        border-radius: 8px;
        background: #ffffff0f;
        color: inherit;
        font-size: 13px;
    }

    :global(.theme_light) .switch_track {
        background: #d3d7de;
    }
    :global(.theme_light) .series_item {
        background: #f7f8fa;
    }
    :global(.theme_light) .series_btn {
        background: #eceef2;
    }
</style>
